import { useState, useEffect } from 'react';

function SavingsComparisonCard({ clusterId, shadowData, fallbackMonthlyCost = 0 }) {
    const [savingsData, setSavingsData] = useState({
        costWithOptOps: 0,
        costWithoutOptOps: 0,
        savings: 0,
        savingsPercent: 0,
        loading: true,
        // True when shadow simulation hasn't produced numbers yet (e.g.
        // cluster added <7 days ago). Caller fed us real cost via
        // `fallbackMonthlyCost`; we render that on both bars + an
        // explanatory note instead of "$0 vs $0" which reads as broken.
        usingFallback: false,
    });

    useEffect(() => {
        // Parent hasn't given us anything to render yet AND no real cost
        // fallback either → genuinely still loading (cluster fetches in
        // flight). Stay on the skeleton until ONE of them arrives. This
        // is the brief-flash window — pretty much always <1s.
        if (!shadowData && !fallbackMonthlyCost) {
            setSavingsData(prev => ({ ...prev, loading: true }));
            return;
        }

        const summary = (shadowData && shadowData.summary) || {};
        const totalActual = Number(summary.total_actual) || 0;
        const totalShadow = Number(summary.total_shadow) || 0;
        const savings = Number(summary.total_savings) || 0;
        const savingsPercent = Number(summary.avg_savings_pct) || 0;

        // Shadow engine hasn't produced numbers for this cluster yet
        // (typical for clusters added <7 days ago, or when auto-opt has
        // never run). Display the real cost from /dashboard/stats so the
        // card communicates "$X spent, savings analysis still gathering"
        // instead of zeros on every bar.
        const shadowHasNumbers = totalShadow > 0 || totalActual > 0 || savings > 0;
        if (!shadowHasNumbers && fallbackMonthlyCost > 0) {
            setSavingsData({
                costWithOptOps: fallbackMonthlyCost,
                costWithoutOptOps: fallbackMonthlyCost,
                savings: 0,
                savingsPercent: 0,
                loading: false,
                usingFallback: true,
            });
            return;
        }

        setSavingsData({
            costWithOptOps: totalActual,
            costWithoutOptOps: totalShadow,
            savings: savings,
            savingsPercent: Math.round(savingsPercent),
            loading: false,
            usingFallback: false,
        });
    }, [shadowData, fallbackMonthlyCost]);

    const fmt = (value) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);

    const barPercent = savingsData.costWithoutOptOps > 0
        ? Math.round((savingsData.costWithOptOps / savingsData.costWithoutOptOps) * 100)
        : 100;

    return (
        <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-surface-border rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h3 className="text-base font-semibold dark:text-white text-slate-900">
                        Savings Comparison
                    </h3>
                    <p className="text-xs text-text-secondary mt-1">
                        {savingsData.usingFallback
                            ? 'Month-to-date cost — savings analysis still gathering'
                            : 'Month-to-date infrastructure costs'}
                    </p>
                </div>
                {!savingsData.loading && savingsData.savings > 0 && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success/10">
                        <span className="material-symbols-outlined text-success text-[16px]">trending_down</span>
                        <span className="text-sm font-bold text-success">{savingsData.savingsPercent}% reduced</span>
                    </div>
                )}
            </div>

            {savingsData.loading ? (
                <div className="animate-pulse space-y-5">
                    <div className="h-12 bg-slate-100 dark:bg-surface-border rounded-lg" />
                    <div className="h-12 bg-slate-100 dark:bg-surface-border rounded-lg" />
                </div>
            ) : (
                <div className="space-y-5">
                    {/* Bar comparison */}
                    <div className="space-y-3">
                        {/* Without OptOps bar */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-slate-500 dark:text-text-secondary">Without OptOps</span>
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 tabular-nums">{fmt(savingsData.costWithoutOptOps)}</span>
                            </div>
                            <div className="h-3 w-full bg-slate-100 dark:bg-surface-border rounded-full overflow-hidden">
                                <div className="h-full bg-slate-400 dark:bg-slate-500 rounded-full transition-all duration-700" style={{ width: '100%' }} />
                            </div>
                        </div>

                        {/* With OptOps bar */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-slate-500 dark:text-text-secondary">With OptOps</span>
                                <span className="text-sm font-semibold dark:text-white text-slate-900 tabular-nums">{fmt(savingsData.costWithOptOps)}</span>
                            </div>
                            <div className="h-3 w-full bg-slate-100 dark:bg-surface-border rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${barPercent}%` }} />
                            </div>
                        </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-surface-border">
                        <div>
                            <p className="text-lg font-bold text-success">{fmt(savingsData.savings)}</p>
                            <p className="text-xs text-text-secondary">Saved this month</p>
                        </div>
                        <div className="sm:border-l border-gray-200 dark:border-surface-border sm:pl-4">
                            <p className="text-lg font-bold dark:text-white text-slate-900">{savingsData.savingsPercent}%</p>
                            <p className="text-xs text-text-secondary">Cost reduction</p>
                        </div>
                        <div className="sm:border-l border-gray-200 dark:border-surface-border sm:pl-4">
                            <p className="text-lg font-bold dark:text-white text-slate-900">{fmt(savingsData.savings * 12)}</p>
                            <p className="text-xs text-text-secondary">Projected yearly</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SavingsComparisonCard;
